import { useCallback, useEffect, useMemo, useState } from 'react'
import { BussinessPlayerAccount, Tag } from '../../../types/commonTypes'
import styles from './PlayerAccountTagEdit.module.css'
import sharedStyles from '../Settings.module.css'
import { TagPill } from '../../../common/components/TagPill/TagPill'
import { CellElement, Table, TableRow } from '../../reports/components/GenericTable'
import { Button } from '../../../common/components/button/Button'
import { Select } from '../../../common/components/Select/Select'
import { PaginationSelectOption, useBusinessSettings } from '../../../contexts/BusinessSettingsContext'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { getIcon } from '../../../common/components/icons/utils'
import { managePlayerAccountTags } from '../../../api/userApiService'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../../routes'
import { useUser } from '../../../contexts/userContext'
import { useNotification } from '../../../contexts/NotificationContext'
import { ManageTagsModal } from './components/ManageTagsModal'
import { ManageTagsFiltersForm } from './components/ManageTagsFilter'
import { useSortedTable } from '../../../hooks/useSortedTable'
import { ReportSortDir } from '../../../contexts/ReportsContextProvider'
import classNames from 'classnames'

export const PlayerAccountTagEdit: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { notifySuccess, notifyError } = useNotification()
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null)
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const {
    businessPlayerAccounts,
    getBusinessPlayerAccountInfo,
    lastPage,
    allPageOptions,
    next,
    prev,
    isFetchingData,
    currentPage,
    pageSize,
    selectPageSize,
    setPage,
  } = useBusinessSettings()
  const { user } = useUser()
  //const [activeSortCol, setActiveSortCol] = useState<SettingsSortBy>()
  const [showManageTags, setShowManageTags] = useState(false)

  const {
    sortedItems: sortedPlayers,
    sortState,
    setSort,
  } = useSortedTable<BussinessPlayerAccount>({
    items: businessPlayerAccounts?.players || [],
    initialSort: { sortBy: 'inviteStatus', direction: ReportSortDir.ASC },
    getSortableValue: (player, key) => {
      const value = player[key]

      if (typeof value === 'string') return value.trim().toLocaleLowerCase()
      if (typeof value === 'number' || typeof value === 'boolean') return value

      // Fallback for undefined/null or unexpected types
      return ''
    },
  })

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
  }, [])

  const getPlayerAccounts = useCallback(
    async (page?: number, search?: string, tags?: Tag[]) => {
      setIsLoading(true)
      const playerAccountResponse = await getBusinessPlayerAccountInfo({ page, search, tags })
      setIsLoading(false)
      return playerAccountResponse
    },
    [getBusinessPlayerAccountInfo],
  )
  useEffect(() => {
    getPlayerAccounts()
  }, [getBusinessPlayerAccountInfo, getPlayerAccounts])

  const handleManagePlayerAccountTags = useCallback(
    async (isRemoving: boolean) => {
      if (!isRemoving) {
        setIsLoading(true)
        const response = await managePlayerAccountTags({ checkedPlayers: selectedPlayerIds, checkedTags: selectedTags })
        setIsLoading(false)
        return response
      }
      if (isRemoving) {
        setIsLoading(true)
        const response = await managePlayerAccountTags({ checkedPlayers: selectedPlayerIds, removedTags: selectedTags })
        setIsLoading(false)
        return response
      }
    },
    [selectedPlayerIds, selectedTags],
  )

  const handleAssign = useCallback(() => {
    handleManagePlayerAccountTags(false).then((response) => {
      if (response?.success) {
        getPlayerAccounts()
        setSelectedTag(null)
        setSelectedTags([])
        setSelectedPlayerIds([])
        notifySuccess({
          title: t('settings_organization.players.tags.assign.notify.success.title', 'Tags added!'),
          content: t('settings_organization.players.tags.assign.notify.success.content', 'Tags added successfully'),
        })
      } else {
        notifyError({
          title: t('settings_organization.players.tags.assign.notify.error.title', 'Adding tags failed'),
          content: t(
            'settings_organization.players.tags.assign.notify.error.content',
            'Please try again later or contact the support',
          ),
        })
      }
    })
  }, [getPlayerAccounts, handleManagePlayerAccountTags, notifyError, notifySuccess, t])

  const handleDeleteTag = useCallback(
    async (tag: Tag, playerId: number) => {
      const response = await managePlayerAccountTags({ checkedPlayers: [playerId], removedTags: [tag] })
      response.success && getPlayerAccounts()
    },
    [getPlayerAccounts],
  )

  const handleBulkDelete = useCallback(() => {
    handleManagePlayerAccountTags(true).then((response) => {
      if (response?.success) {
        getPlayerAccounts()
        setSelectedTag(null)
        setSelectedTags([])
        setSelectedPlayerIds([])
        notifySuccess({
          title: t('settings_organization.players.tags.remove.notify.success.title', 'Tags removed!'),
          content: t('settings_organization.players.tags.remove.notify.success.content', 'Tags removed successfully'),
        })
      } else {
        notifyError({
          title: t('settings_organization.players.tags.remove.notify.error.title', 'Removing tags failed'),
          content: t(
            'settings_organization.players.tags.remove.notify.error.content',
            'Please try again later or contact the support',
          ),
        })
      }
    })
  }, [getPlayerAccounts, handleManagePlayerAccountTags, notifyError, notifySuccess, t])

  const handleOnManageTags = useCallback(() => {
    setShowManageTags(true)
  }, [])

  const handleRemoveSelectedTag = useCallback((tag: Tag) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tag.id))
  }, [])

  const handleOnCancel = useCallback(() => {
    navigate(routes.organizationSettings)
  }, [navigate])

  const toggleSelected = useCallback(
    (id: number) => {
      if (selectedPlayerIds.includes(id)) setSelectedPlayerIds((prev) => prev.filter((p) => p !== id))
      else setSelectedPlayerIds((prev) => prev.concat(id))
    },
    [selectedPlayerIds],
  )

  const onChangePageSize = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      selectPageSize(option!.value)
    },
    [selectPageSize],
  )

  const onChangeCurrentPage = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      setPage(option!.value)
    },
    [setPage],
  )

  const headerLabels = ['Name', 'Email', 'Tags']
  const headerWidths = ['35%', '35%', '30%']

  const getTagTableRow = useCallback((): TableRow[] => {
    const rows: TableRow[] = []
    sortedPlayers.forEach((player) => {
      const row = player as BussinessPlayerAccount
      const tableRow: CellElement[] = [
        row.id,
        row.nickName,
        row.email,
        <div className={styles.tagPillContainer}>
          {row.tags.map((tag) => (
            <TagPill size='small' key={tag.id} label={tag.label} onDelete={() => handleDeleteTag(tag, player.id)} />
          ))}
        </div>,
      ]
      rows.push(tableRow)
    })
    return rows
  }, [handleDeleteTag, sortedPlayers])

  const tagTableRowData = useMemo((): TableRow[] => {
    if (businessPlayerAccounts) return getTagTableRow()
    const rows: TableRow[] = []
    return rows
  }, [businessPlayerAccounts, getTagTableRow])

  const onFilterChange = useCallback(
    (tag: Tag | null) => {
      tag ? getPlayerAccounts(1, '', [tag]) : getPlayerAccounts()
    },
    [getPlayerAccounts],
  )

  if (!user) {
    return null
  }

  return (
    <div className={styles.section}>
      <div className={styles.titleContainer}>
        <span className={classNames(sharedStyles.icon)} onClick={handleOnCancel}>
          {getIcon('arrowLeft')}
        </span>
        <h1 className={sharedStyles.sectionTitle}>
          {t('settings_organization.players.tags.title', 'Edit player account tags')}
        </h1>
      </div>
      <ManageTagsFiltersForm allTags={user?.tags} onChange={onFilterChange} />
      <Table
        isSortable
        isSelectable
        hasPagination
        headerWidths={headerWidths}
        headerLabels={headerLabels}
        onToggleRowSelected={toggleSelected}
        selectedRows={selectedPlayerIds}
        rows={tagTableRowData}
        currentPage={currentPage}
        pageSize={pageSize}
        lastPage={lastPage}
        allPageOptions={allPageOptions}
        onClickNext={next}
        onClickPrev={prev}
        onChangePageSize={onChangePageSize}
        onChangeCurrentPage={onChangeCurrentPage}
        itemsPerPageLabel={t('settings_organization.players.tags.per_page', 'player accounts per page')}
        loading={isFetchingData || isLoading}
        pageSizeOptions={[{ value: 30, label: '30' }]}
        limitRowsSelected={false}
        headerSortTags={['nickName', 'email', 'tags']}
        onSort={(sortKey: string) => (direction: string) => {
          setSort(sortKey as keyof BussinessPlayerAccount, direction as ReportSortDir)
        }}
        activeSortCol={sortState.sortBy}
        noSortIndexes={[false, false, true, false, true]}
      />
      <div className={styles.actions}>
        <div className={styles.topActions}>
          <div className={styles.leftActions}>
            <Select
              key={`my_unique_select_key__${selectedTag}`}
              options={user.tags.map((tag) => ({ value: tag.id, label: tag.label }))}
              value={
                selectedTag
                  ? {
                      value: selectedTag.id,
                      label:
                        selectedTag.label.length > 15 ? selectedTag.label.substring(0, 15) + '...' : selectedTag.label,
                    }
                  : undefined
              }
              onChange={(option) => {
                const tag = user.tags.find((t) => t.id === option?.value)
                setSelectedTag(tag ?? null)
                tag && !selectedTags.includes(tag) && setSelectedTags((prev) => [...prev, tag])
              }}
              placeholder={t('settings_organization.players.tags.buttons.select_tag', 'Select tag')}
              className={styles.tagSelect}
            />
            <div className={styles.selectedTags}>
              {selectedTags.map((tag) => (
                <TagPill size='small' key={tag.id} label={tag.label} onDelete={() => handleRemoveSelectedTag(tag)} />
              ))}
            </div>
          </div>
          {getIcon('arrowRight')}
          <div className={styles.rightActions}>
            <Button
              onClick={handleAssign}
              variant='tiny'
              disabled={selectedTags.length === 0 || selectedPlayerIds.length === 0}
            >
              {t('settings_organization.players.tags.buttons.assign', 'Assign')}
            </Button>
            <Button
              onClick={handleBulkDelete}
              variant='tiny'
              disabled={selectedTags.length === 0 || selectedPlayerIds.length === 0}
            >
              {t('settings_organization.players.tags.buttons.delete_tag', 'Delete')}
            </Button>
          </div>
        </div>
        <div className={styles.bottomActions}>
          <Button onClick={handleOnManageTags}>
            {t('settings_organization.players.tags.buttons.manage_tags', 'Manage tags')}
          </Button>
          <Button onClick={handleOnCancel} variant='outline-normal'>
            {t('settings_organization.players.tags.buttons.return', 'Return')}
          </Button>
        </div>
      </div>
      {showManageTags && <ManageTagsModal onClose={() => setShowManageTags(false)} />}
    </div>
  )
}
